import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Typography, Table, Input, Space, Dropdown, Button, App, Tooltip, Select } from 'antd';
import { MoreOutlined, EditOutlined, DeleteOutlined, PlusOutlined, EyeOutlined, LockOutlined, SearchOutlined, ClearOutlined, FileExcelOutlined } from '@ant-design/icons';
import { listClientRoles, listCompetitionRegions, listGenders, listPlans } from '../../services/catalogs';
import { DatePicker } from 'antd';
import { deleteClient, listClients, updateClientStatus } from '../../services/participant';
import { Tag } from 'antd';
import dayjs from 'dayjs';
import usePermissions from '../../hooks/usePermissions';
import { exportToExcel } from '../../utils/excel';

const { Title } = Typography;
const { useApp } = App;

const ParticipantList = () => {
  const navigate = useNavigate();
  const { message, modal } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const { can } = usePermissions();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState(null);
  const [roleFilter, setRoleFilter] = useState(null);
  const [genderFilter, setGenderFilter] = useState(null);
  const [regionFilter, setRegionFilter] = useState(null);
  const [startDate, setStartDateFilter] = useState(null);

  const [draftSearch, setDraftSearch] = useState('');
  const [draftPlan, setDraftPlan] = useState(null);
  const [draftRole, setDraftRole] = useState(null);
  const [draftGender, setDraftGender] = useState(null);
  const [draftRegion, setDraftRegion] = useState(null);
  const [draftStarDate, setDraftStarDate] = useState(null);
  const [queryKey, setQueryKey] = useState(0);

  // Catálogos para los filtros
  const [plans, setPlans] = useState([]);
  const [genders, setGenders] = useState([]);
  const [regions, setRegions] = useState([]);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    const loadCatalogs = async () => {
          try {
            const [
              plansData,
              gendersData,
              regionsData,
              rolesData
            ] = await Promise.all([
              listPlans(),
              listGenders(),
              listCompetitionRegions(),
              listClientRoles(),
            ]);
            setPlans(plansData || []);
            setGenders(gendersData || []);
            setRegions(regionsData || []);
            setRoles(rolesData || []);
          } catch (error) {
            console.error('Error al cargar catálogos:', error);
            message.error('No se pudieron cargar los catálogos');
          }
        };
    loadCatalogs();
  }, [message]);

  const fetchData = useCallback(async (page, pageSize, srt, q, planId, roleId, genderId, regionId, startDate) => {
    setLoading(true);
    try {
      const params = {
        search: q || undefined,
        page: page || pagination.current,
        pageSize: pageSize || pagination.pageSize,
        sortField: srt?.field,
        sortOrder: srt?.order,
        ...(planId && { planId: planId }),
        ...(roleId && { roleId: roleId }),
        ...(genderId && { genderId: genderId }),
        ...(regionId && { regionId: regionId }),
        ...(startDate && { startDate: startDate }),
      };

      const { items, total } = await listClients(params);
      setData(items || []);
      setPagination(prev => ({
        ...prev,
        current: page || prev.current,
        pageSize: pageSize || prev.pageSize,
        total: total || 0
      }));
    } catch (err) {
      console.error(err);
      message.error('No se pudo cargar la lista de participantes.');
    } finally {
      setLoading(false);
    }
  }, [pagination, message]);

  useEffect(() => {
    fetchData(1, pagination.pageSize, null, search, planFilter, roleFilter, genderFilter, regionFilter, queryKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(pag.current, pag.pageSize, srt, search, planFilter, roleFilter, genderFilter, regionFilter, startDate, queryKey);
  };

  const onSearch = (e) => {
    const value = e.target.value;
    setDraftSearch(value || '');
  };

  const handlePlanFilter = (value) => {
    setDraftPlan(value);
    // fetchData(1, pagination.pageSize, sorter, search, value, roleFilter, genderFilter, regionFilter, startDate);
  };

  const handleRoleFilter = (value) => {
    setDraftRole(value);
    // fetchData(1, pagination.pageSize, sorter, search, planFilter, value, genderFilter, regionFilter, startDate);
  };

  const handleGenderFilter = (value) => {
    setDraftGender(value);
    // fetchData(1, pagination.pageSize, sorter, search, planFilter, roleFilter, value, regionFilter, startDate);
  };

  const handleRegionFilter = (value) => {
    setDraftRegion(value);
    // fetchData(1, pagination.pageSize, sorter, search, planFilter, roleFilter, genderFilter, value, startDate);
  };

  const handleStartDateFilter = (date, dateString) => {
    setDraftStarDate(dateString);
    // fetchData(1, pagination.pageSize, sorter, search, planFilter, roleFilter, genderFilter, regionFilter, dateString);
  };

  const applyFilters = ({
    search = '',
    plan = null,
    role = null,
    gender = null,
    region = null,
    startDate = null,
  } = {}) => {
    setSearch(search);
    setPlanFilter(plan);
    setRoleFilter(role);
    setGenderFilter(gender);
    setRegionFilter(region);
    setStartDateFilter(startDate);
    setQueryKey(prev => prev + 1);

    fetchData( 1, pagination.pageSize, sorter, search, plan, role, gender, region, startDate );
  };


  const handleSearch = () => {
        
    applyFilters({
        search: draftSearch,
        plan: draftPlan,
        role: draftRole,
        gender: draftGender,
        region: draftRegion,
        startDate: draftStarDate,
    });

  };

  const handleClearFilters = () => {
    // Limpiar draft
    setDraftSearch('');
    setDraftPlan(null);
    setDraftRole(null);
    setDraftGender(null);
    setDraftRegion(null);
    setDraftStarDate(null);

    applyFilters();

  };

  const handleMenuAction = (action, record) => {
    switch (action) {
      case 'view':
        navigate(`/participants/${record.id}/view`);
        break;
      case 'edit':
        navigate(`/participants/${record.id}/edit`);
        break;
      case 'toggle':
        const willActivate = !record.isActive;
        modal.confirm({
        title: willActivate ? '¿Activar Participante?' : '¿Desactivar Participante?',
        content: `${record.firstName || record.email || 'Participante'}`,
        okText: willActivate ? 'Activar' : 'Desactivar',
        cancelText: 'Cancelar',
        onOk: async () => {
            try {
            await updateClientStatus(record.id, willActivate);
            message.success(willActivate ? 'Participante activado' : 'Participante desactivado');
            fetchData(pagination.current, pagination.pageSize, sorter, search);
            } catch {
            message.error('No se pudo actualizar el estatus');
            }
        },
        });
        break;
      case 'delete':
        modal.confirm({
          title: '¿Eliminar participante?',
          content: `${record.firstName || 'participante'}`,
          okText: 'Eliminar',
          okButtonProps: { danger: true },
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
                const response = await deleteClient(record.id);
                message.success(response.data?.data?.message || 'participante eliminado');

                const nextPage = (data.length === 1 && pagination.current > 1)
                  ? pagination.current - 1
                  : pagination.current;
                setPagination(prev => ({ ...prev, current: nextPage }));
                fetchData(nextPage, pagination.pageSize, sorter, search, planFilter, roleFilter, genderFilter, regionFilter);
            } catch {
              message.error('No se pudo eliminar la participante');
            }
          },
        });
        break;
      default:
        break;
    }
  };

  const getActionsMenu = (record) => {
    const menuItems = [
      can('participants.view') && {
        key: 'view',
        icon: <EyeOutlined />,
        label: 'Ver',
      },
      can('participants.edit') && {
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Editar',
      },
      can('participants.toggle') && {
        key: 'toggle',
        icon: <LockOutlined />,
        label: record.isActive ? 'Desactivar' : 'Activar',
      },
      {
        type: 'divider',
      },
      can('participants.delete') && {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Borrar usuario',
        danger: true,
      },
    ];

    return {
      items: menuItems,
      onClick: ({ key }) => handleMenuAction(key, record),
    };
  };

  const formatReferredByCode = (code) => (code ? code : 'Sin referido');

  const handleExportToExcel = async () => {
    setExporting(true);
    try {
      const exportPageSize = 500;
      const baseParams = {
        search: search || undefined,
        sortField: sorter?.field,
        sortOrder: sorter?.order,
        ...(planFilter && { planId: planFilter }),
        ...(roleFilter && { roleId: roleFilter }),
        ...(genderFilter && { genderId: genderFilter }),
        ...(regionFilter && { regionId: regionFilter }),
        ...(startDate && { startDate }),
      };

      const allItems = [];
      let page = 1;
      let total = Infinity;

      while (allItems.length < total) {
        const { items, total: pageTotal } = await listClients({
          ...baseParams,
          page,
          pageSize: exportPageSize,
        });

        if (!items || items.length === 0) break;

        allItems.push(...items);
        total = Number(pageTotal) || allItems.length;
        page += 1;

        // Evita bucle infinito si el API no pagina bien
        if (items.length < exportPageSize) break;
      }

      if (allItems.length === 0) {
        message.warning('No hay participantes para exportar');
        return;
      }

      const excelData = allItems.map((participant) => {
        const fullName = [participant.firstName, participant.lastName, participant.secondLastName]
          .filter(Boolean)
          .join(' ')
          .trim();
        const profile =
          (participant.role?.name || participant.role?.description || participant.role?.code || '-') +
          (participant.roleId === 3 ? `(${participant.subRole?.name || '-'})` : '');

        return {
          'Nombre': fullName || '-',
          'Correo electrónico': participant.email || '-',
          'Perfil': profile,
          'Membresía': participant.subscription?.[0]?.plan?.code || participant.subscription?.[0]?.plan?.name || '-',
          'Género': participant.gender?.description || participant.gender?.code || '-',
          'Región': participant.state?.regionStates?.[0]?.region?.description || participant.state?.regionStates?.[0]?.region?.code || '-',
          'Fecha de registro': participant.createdAt ? dayjs(participant.createdAt).format('DD/MM/YYYY') : '-',
          'Estado': participant.isActive ? 'Activo' : 'Inactivo',
          'Código de referido': formatReferredByCode(participant.referredByCode),
        };
      });

      const columnWidths = [
        { wch: 30 },
        { wch: 35 },
        { wch: 25 },
        { wch: 18 },
        { wch: 12 },
        { wch: 18 },
        { wch: 18 },
        { wch: 12 },
        { wch: 20 },
      ];

      exportToExcel(excelData, columnWidths, 'Participantes', 'reporte_participantes');
    } catch (err) {
      console.error('Error al exportar a Excel:', err);
      message.error('No se pudo exportar los participantes a Excel');
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'firstName',
      key: 'firstName',
      render: (text) => text || '-',
      sorter: true,
    },
    {
      title: 'Correo electrónico',
      dataIndex: 'email',
      key: 'email',
      render: (text) => text || '-',
      sorter: true,
    },
    {
      title: 'Perfil',
      dataIndex: 'role',
      key: 'role',
      render: (_, r) => ((r.role?.name || r.role?.description || r.role?.code || '-') + (r.roleId === 3 ? `(${r.subRole?.name || '-'})` : '')),
      sorter: true,
    },
    {
      title: 'Membresía',
      dataIndex: 'subscription',
      key: 'subscription',
      render: (_, r) => r.subscription[0]?.plan?.code || r.subscription[0]?.plan?.name || '-',
      sorter: false,
    },
    {
      title: 'Género',
      dataIndex: 'gender',
      key: 'gender',
      render: (_, r) => r.gender?.description || r.gender?.code || '-',
      sorter: true,
    },
    {
      title: 'Región',
      dataIndex: 'region',
      key: 'region',
      render: (_, r) => r.state?.regionStates[0]?.region?.description || r.state?.regionStates[0]?.region?.code || '-',
      sorter: false,
    },
    {
      title: 'Fecha Ingreso',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => text? dayjs(text).format('DD/MM/YYYY') : '-',
      sorter: true,
    },
    {
      title: 'Estatus',
      dataIndex: 'isActive',
      key: 'isActive',
      sorter: true,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'volcano'}>
          {isActive ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    {
      title: 'Código de referido',
      dataIndex: 'referredByCode',
      key: 'referredByCode',
      render: (code) => formatReferredByCode(code),
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Dropdown menu={getActionsMenu(record)} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: '16px' }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div className="header-container">
              <Title level={3} style={{ marginBottom: 0 }}>Participantes</Title>
              <Space>
                {can('participants.export') && (
                  <Tooltip title="Exportar a Excel">
                    <Button
                      className='btn-add'
                      icon={<FileExcelOutlined />}
                      onClick={handleExportToExcel}
                      loading={exporting}
                      disabled={exporting}
                      aria-label="Exportar a Excel"
                    />
                  </Tooltip>
                )}
                {can('participants.add') && (<Tooltip title="Crear participante">
                  <Button
                    type="primary"
                    className='btn-add'
                    icon={<PlusOutlined />}
                    aria-label="Crear participante"
                    onClick={() => navigate('/participants/add')}
                  />
                </Tooltip>)}
              </Space>
            </div>
      <Card
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <Input
              placeholder="Buscar"
              allowClear
              value={draftSearch}
              onChange={onSearch}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Membresía"
              allowClear
              style={{ width: 150 }}
              value={draftPlan}
              onChange={handlePlanFilter}
              options={plans.map(plan => ({
                value: plan.id,
                label: plan.code || plan.description,
              }))}
            />
            <Select
              placeholder="Perfil"
              allowClear
              style={{ width: 150 }}
              value={draftRole}
              onChange={handleRoleFilter}
              options={roles.map(role => ({
                value: role.id,
                label: role.name || role.code,
              }))}
            />
            <Select
              placeholder="Género"
              allowClear
              style={{ width: 150 }}
              value={draftGender}
              onChange={handleGenderFilter}
              options={genders.map(gender => ({
                value: gender.id,
                label: gender.description || gender.code,
              }))}
            />
            <Select
              placeholder="Región"
              allowClear
              style={{ width: 150 }}
              value={draftRegion}
              onChange={handleRegionFilter}
              options={regions.map(region => ({
                value: region.id,
                label: region.description || region.code,
              }))}
            />
            <DatePicker
              placeholder="Fecha de inicio"
              style={{ width: 150 }}
              value={draftStarDate ? dayjs(draftStarDate, 'YYYY-MM-DD') : null}
              onChange={handleStartDateFilter}
            />
            <Space>
              <Tooltip title="Buscar">
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' }}
                  shape="circle"
                  size="large"
                />
              </Tooltip>
              <Tooltip title="limpiar filtros">
                <Button
                  icon={<ClearOutlined />}
                  onClick={handleClearFilters}
                  shape="circle"
                  size="large"
                />
              </Tooltip>
            </Space>
          </Space>
          <Table
            rowKey={(r) => r.id ?? r._id}
            columns={columns}
            dataSource={data}
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (total) => `Total: ${total} participantes`
            }}
            onChange={onChange}
          />
        </Space>
      </Card>
    </div>
  );
};

export default ParticipantList;

